import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { localDateString } from '../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAttendanceReport } from '../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
// Attendance events are keyed on user_id, so the picker lists employees that
// have a login (sourceType: 'EMPLOYEE'). The plain users dropdown returned
// every society user, which put flat owners and tenants in this staff report.
import { getUsernamesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import { PaginationLimit } from '../../../components/PaginationLimit';

const ACTION_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Login (check-in)', value: 'login' },
    { label: 'Logout (check-out)', value: 'logout' },
];

const GEOFENCE_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Inside premises', value: 'true' },
    { label: 'Outside premises', value: 'false' },
];

const initialFilters = {
    // Filter directly by user_id — matches how the attendance table is keyed
    // (login-history rows carry user_id, not employee_id).
    user_id: '',
    action_type: '',
    within_geofence: '',
    from: '',
    to: '',
};

const AttendanceReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { records, pagination, loading } = useSelector(state => state.AttendanceReducer || {});
    const { usernamesDropdownData, usernamesDropdownLoading } = useSelector(state => state.EmployeeReducer || {});

    const [filters, setFilters] = useState(initialFilters);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (societyId) dispatch(getUsernamesDropdown({ societyId, sourceType: 'EMPLOYEE' }));
    }, [dispatch, societyId]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const handleReset = () => {
        setFilters(initialFilters);
        setPage(1);
    };

    const pg = pagination || {};

    // Employee dropdown options — /employees/login-report/users only returns
    // employees that have a login, so every row carries user_id. Filtering
    // attendance by the picked user_id lines up 1:1 with what's stored on
    // the login_history rows.
    const userOptions = useMemo(() => {
        const list = Array.isArray(usernamesDropdownData) ? usernamesDropdownData : [];
        return list.map(u => ({
            value: String(u.user_id),
            label: u.name || u.label || u.email || `User #${u.user_id}`,
            sub: u.role?.role_name || u.username || u.email || '',
        }));
    }, [usernamesDropdownData]);

    const rows = records || [];

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.user_id) params.user_id = filters.user_id;
        if (filters.action_type) params.action_type = filters.action_type;
        if (filters.within_geofence !== '') params.within_geofence = filters.within_geofence;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;
        dispatch(getAttendanceReport(params));
    }, [
        dispatch, societyId, page, limit,
        filters.user_id, filters.action_type, filters.within_geofence,
        filters.from, filters.to
    , refreshKey]);

    const stats = useMemo(() => {
        const today = localDateString();
        const todayEvents = rows.filter(r => (r.created_at || '').slice(0, 10) === today);
        const uniqueUsers = new Set(rows.filter(r => r.user_id).map(r => r.user_id));
        const outside = rows.filter(r => r.is_within_geofence === false).length;
        return {
            total: pg.total ?? rows.length,
            todayCount: todayEvents.length,
            uniqueEmployees: uniqueUsers.size,
            outsideGeofence: outside,
        };
    }, [rows, pg.total]);

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const geofenceBadge = (within) => {
        if (within === true) {
            return (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-emerald-50 tw-text-emerald-700">
                    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-500" /> Inside
                </span>
            );
        }
        if (within === false) {
            return (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-red-50 tw-text-red-700">
                    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-red-500" /> Outside
                </span>
            );
        }
        return (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-gray-100 tw-text-gray-500">
                N/A
            </span>
        );
    };

    const actionBadge = (action) => {
        const isLogin = action === 'login';
        return (
            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${isLogin ? 'tw-bg-blue-50 tw-text-blue-700' : 'tw-bg-purple-50 tw-text-purple-700'}`}>
                <Icon icon={isLogin ? 'mdi:login' : 'mdi:logout'} className="tw-text-xs" />
                {isLogin ? 'Check-in' : 'Check-out'}
            </span>
        );
    };


    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.user_id) params.user_id = filters.user_id;
            if (filters.action_type) params.action = filters.action_type; // BE reads `action`, not `action_type`
            if (filters.within_geofence !== '') params.within_geofence = filters.within_geofence;
            if (filters.from) params.from = filters.from;
            if (filters.to) params.to = filters.to;

            const res = await axiosInstance.get(apiEndpoints.SOCIETY_EXPORT_ATTENDANCE_REPORT_API, {
                params,
                responseType: 'blob',
            });
            const blob = res instanceof Blob
                ? res
                : new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const from = filters.from || 'all';
            const to = filters.to || 'all';
            a.download = `attendance_${societyId}_${from}_to_${to}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to export attendance');
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        { header: 'Sr No', width: '50px', align: 'center' },
        { header: 'Date & Time' },
        { header: 'Employee' },
        { header: 'Department' },
        { header: 'Action', align: 'center' },
        { header: 'Location' },
        { header: 'Distance', align: 'right' },
        { header: 'Geofence', align: 'center' },
        { header: 'Device' },
        { header: 'Session' },
    ];

    const StatCard = ({ icon, color, label, value }) => (
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-p-3 tw-flex tw-items-center tw-gap-2.5">
            <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color}`}>
                <Icon icon={icon} className="tw-text-white tw-text-base" />
            </div>
            <div className="tw-min-w-0">
                <div className="tw-text-base tw-font-bold tw-text-gray-800 tw-leading-tight">{value}</div>
                <div className="tw-text-[10px] tw-text-gray-500 tw-truncate">{label}</div>
            </div>
        </div>
    );

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Attendance Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Employee login/logout events with location &amp; geofence status</p>
                </div>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting || loading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-rounded-md hover:tw-bg-emerald-50 disabled:tw-opacity-50"
                >
                    <Icon icon={exporting ? 'mdi:loading' : 'mdi:file-download-outline'} className={exporting ? 'mdi-spin' : ''} />
                    {exporting ? 'Preparing…' : 'Download Excel'}
                </button>
            </div>

            {/* Summary stats — reflects the currently filtered page. */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                <StatCard icon="mdi:file-document-multiple-outline" color="tw-bg-blue-500"
                          label="Total events (filtered)" value={stats.total} />
                <StatCard icon="mdi:calendar-today-outline" color="tw-bg-emerald-500"
                          label="Today (this page)" value={stats.todayCount} />
                <StatCard icon="mdi:account-multiple-outline" color="tw-bg-purple-500"
                          label="Unique employees (this page)" value={stats.uniqueEmployees} />
                <StatCard icon="mdi:map-marker-alert-outline" color="tw-bg-rose-500"
                          label="Outside geofence (this page)" value={stats.outsideGeofence} />
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Employee</label>
                        <select
                            value={filters.user_id}
                            onChange={(e) => handleFilterChange('user_id', e.target.value)}
                            disabled={usernamesDropdownLoading}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            <option value="">{usernamesDropdownLoading ? 'Loading…' : 'All employees'}</option>
                            {userOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}{o.sub ? ` — ${o.sub}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Action</label>
                        <select
                            value={filters.action_type}
                            onChange={(e) => handleFilterChange('action_type', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Geofence</label>
                        <select
                            value={filters.within_geofence}
                            onChange={(e) => handleFilterChange('within_geofence', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            {GEOFENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From</label>
                        <input
                            type="date"
                            value={filters.from}
                            onChange={(e) => handleFilterChange('from', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To</label>
                        <input
                            type="date"
                            value={filters.to}
                            onChange={(e) => handleFilterChange('to', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button
                        onClick={handleReset}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:refresh" className="tw-text-sm" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {columns.map(col => (
                                    <th
                                        key={col.header}
                                        className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: col.align || 'left', minWidth: col.width || 'auto' }}
                                    >
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:map-marker-off-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No attendance records found</p>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">Users appear here after they log in through the app.</p>
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, idx) => (
                                    <tr key={r.id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-400">{(page - 1) * limit + idx + 1}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{formatDateTime(r.created_at)}</td>
                                        <td className="tw-px-3 tw-py-2">
                                            <div className="tw-font-medium tw-text-gray-800">{r.employee?.name || r.user?.name || '-'}</div>
                                            <div className="tw-text-[10px] tw-text-gray-400">
                                                {r.employee?.employee_code || r.user?.email || '-'}
                                            </div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                            {r.employee?.department || '-'}
                                            {r.employee?.designation ? (
                                                <div className="tw-text-[10px] tw-text-gray-400">{r.employee.designation}</div>
                                            ) : null}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center">{actionBadge(r.action)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                            {r.latitude != null && r.longitude != null
                                                ? `${Number(r.latitude).toFixed(5)}, ${Number(r.longitude).toFixed(5)}`
                                                : '-'}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">
                                            {r.distance_meters != null ? `${Math.round(r.distance_meters)} m` : '-'}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center">{geofenceBadge(r.is_within_geofence)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                            {r.device_type || '-'}
                                            {r.platform ? <div className="tw-text-[10px] tw-text-gray-400">{r.platform}</div> : null}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-400 tw-font-mono tw-text-[10px]" title={r.session_id || ''}>
                                            {r.session_id ? `${String(r.session_id).slice(0, 8)}…` : '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pg.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pg.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default AttendanceReport;
